// ---------------------------------------------------------------------------
// Comparison Table Renderer
// ---------------------------------------------------------------------------

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";

interface ComparisonColumn {
  label: string;
  isHighlighted?: boolean;
  badgeText?: string;
}

interface ComparisonRow {
  feature: string;
  values: (string | boolean)[];
}

interface ComparisonTableProps {
  heading?: string;
  columns: ComparisonColumn[];
  rows: ComparisonRow[];
  ctaText?: string;
  ctaUrl?: string;
}

function renderCellValue(value: string | boolean): string {
  if (typeof value === "boolean") {
    return value
      ? `<span class="ease-comparison-check" aria-label="Yes">&#10003;</span>`
      : `<span class="ease-comparison-cross" aria-label="No">&#10007;</span>`;
  }
  return text(String(value));
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as unknown as ComparisonTableProps;

  const heading = p.heading ?? "Compare Plans";
  const columns = Array.isArray(p.columns) ? p.columns : [];
  const rows = Array.isArray(p.rows) ? p.rows : [];

  const headingHtml = `<h2 class="ease-comparison-heading">${text(heading)}</h2>`;

  // Header row
  const headerCells = columns
    .map((col) => {
      const highlightClass = col.isHighlighted ? " ease-comparison-col--highlighted" : "";
      const badge = col.badgeText
        ? `<span class="ease-comparison-badge">${text(col.badgeText)}</span> `
        : "";
      return `      <th class="ease-comparison-header${highlightClass}" scope="col">${badge}${text(col.label)}</th>`;
    })
    .join("\n");

  // Data rows
  const bodyRows = rows
    .map((row) => {
      const values = Array.isArray(row.values) ? row.values : [];
      const valueCells = values
        .map((val, idx) => {
          const col = columns[idx];
          const highlightClass = col?.isHighlighted ? " ease-comparison-col--highlighted" : "";
          return `      <td class="ease-comparison-cell${highlightClass}">${renderCellValue(val)}</td>`;
        })
        .join("\n");

      // Pad missing cells so every row has the same number of <td> elements
      const remainingCols = columns.length - values.length;
      let paddingCells = "";
      for (let pad = 0; pad < remainingCols; pad++) {
        const col = columns[values.length + pad];
        const highlightClass = col?.isHighlighted ? " ease-comparison-col--highlighted" : "";
        paddingCells += `\n      <td class="ease-comparison-cell${highlightClass}">\u2014</td>`;
      }

      return `    <tr>
      <th class="ease-comparison-feature" scope="row">${text(row.feature)}</th>
${valueCells}${paddingCells}
    </tr>`;
    })
    .join("\n");

  const ctaHtml = p.ctaText
    ? `\n  <div class="ease-comparison-cta-wrap" style="text-align: center; margin-top: var(--ease-spacing-lg, 2rem);">
    <a href="${url(p.ctaUrl ?? "#form")}" class="ease-btn ease-btn-primary">${text(p.ctaText)}</a>
  </div>`
    : "";

  return `<section class="ease-section ease-comparison-table" aria-label="${attr(heading)}">
  ${headingHtml}
  <div class="ease-comparison-wrap" style="overflow-x: auto;">
    <table class="ease-comparison" style="width: 100%; border-collapse: collapse;">
      <thead>
        <tr>
          <th class="ease-comparison-corner" scope="col"></th>
${headerCells}
        </tr>
      </thead>
      <tbody>
${bodyRows}
      </tbody>
    </table>
  </div>${ctaHtml}
</section>`;
}

registerRenderer("comparison-table", { render });
